import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { analyticsApi, assetUrl } from '../utils/api';
import { IconArrowRight } from '../components/Icons';

const SECONDARY = '#e8a838';
const NAV_CARD = '#162231';
const BORDER_COLOR = '#2a3f56';
const TEXT_LIGHT = '#f0f2f5';
const SURFACE = '#1e2d3d';

function MetricTile({ label, value, sub }) {
  return (
    <div style={{
      background: NAV_CARD, border: `1px solid ${BORDER_COLOR}`, borderRadius: 10,
      padding: '20px 24px', flex: '1 1 160px', minWidth: 140
    }}>
      <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8a9bb5', marginBottom: 8, fontFamily: 'IBM Plex Sans, sans-serif' }}>
        {label}
      </div>
      <div style={{ fontSize: '2rem', fontWeight: 700, color: TEXT_LIGHT, fontFamily: 'Oswald, sans-serif', lineHeight: 1 }}>
        {value ?? '—'}
      </div>
      {sub && <div style={{ fontSize: '0.78rem', color: '#8a9bb5', marginTop: 4, fontFamily: 'IBM Plex Sans, sans-serif' }}>{sub}</div>}
    </div>
  );
}

export default function SponsorReport() {
  const { eventId, sponsorId } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    analyticsApi.getSponsorReport(eventId, sponsorId)
      .then(res => setReport(res.data))
      .catch(err => setError(err.response?.data?.error || 'Failed to load report'))
      .finally(() => setLoading(false));
  }, [eventId, sponsorId]);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;
  if (error) return <div style={{ padding: 40, color: '#ef4444', fontFamily: 'IBM Plex Sans, sans-serif' }}>{error}</div>;
  if (!report) return null;

  const { event, sponsor, metrics } = report;
  const startDate = event.start_date ? new Date(event.start_date).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : '';
  const endDate = event.end_date ? new Date(event.end_date).toLocaleDateString('en-CA', { year: 'numeric', month: 'long', day: 'numeric' }) : '';

  return (
    <>
      {/* Report content */}
      <div className="sponsor-report-page" style={{ maxWidth: 720, margin: '0 auto', paddingTop: 28, fontFamily: 'IBM Plex Sans, sans-serif', color: TEXT_LIGHT }}>

        {/* Header */}
        <div style={{ borderBottom: `4px solid ${SECONDARY}`, paddingBottom: 20, marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, flexWrap: 'wrap' }}>
            {sponsor.logo_url && (
              <img
                src={assetUrl(sponsor.logo_url)}
                alt={sponsor.sponsor_name}
                style={{ height: 64, width: 'auto', maxWidth: 200, objectFit: 'contain' }}
                onError={e => { e.target.style.display = 'none'; }}
              />
            )}
            <div>
              <h1 style={{ fontFamily: 'Oswald, sans-serif', fontSize: '1.8rem', fontWeight: 700, margin: 0, color: TEXT_LIGHT }}>
                {sponsor.sponsor_name}
              </h1>
              <div style={{ fontSize: '0.85rem', color: '#8a9bb5', marginTop: 4 }}>Sponsor Performance Report</div>
            </div>
          </div>
          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div style={{ fontSize: '1.1rem', fontWeight: 600, fontFamily: 'Oswald, sans-serif', color: TEXT_LIGHT }}>{event.name}</div>
              <div style={{ fontSize: '0.85rem', color: '#8a9bb5', marginTop: 2 }}>
                {startDate}{endDate && startDate !== endDate ? ` – ${endDate}` : ''}
              </div>
            </div>
            <div className="no-print" style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
              <button
                onClick={() => navigate(`/events/${eventId}`, { state: { tab: 'analytics' } })}
                style={{
                  background: 'none', color: TEXT_LIGHT, border: `1px solid ${BORDER_COLOR}`, borderRadius: 6,
                  padding: '7px 14px', cursor: 'pointer', fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: 600, fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center', gap: 6
                }}
              >
                <span style={{ transform: 'scaleX(-1)', display: 'inline-flex' }}><IconArrowRight size="0.9em" /></span>
                Back to Analytics
              </button>
              <button
                onClick={() => window.print()}
                style={{
                  background: SECONDARY, color: '#0d1b2a', border: 'none', borderRadius: 6,
                  padding: '8px 16px', cursor: 'pointer', fontFamily: 'IBM Plex Sans, sans-serif',
                  fontWeight: 600, fontSize: '0.8rem'
                }}
              >
                Print / Save as PDF
              </button>
            </div>
          </div>
        </div>

        {/* Main metric tiles */}
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 28 }}>
          <MetricTile
            label="Scoreboard Viewers"
            value={metrics.scoreboard_unique_viewers?.toLocaleString()}
            sub="unique IPs — your eyeballs"
          />
          <MetricTile
            label="Logo Clicks"
            value={metrics.logo_unique_clickers?.toLocaleString()}
            sub="unique visitors who clicked"
          />
          <MetricTile
            label="Website Visits"
            value={metrics.website_unique_redirectors?.toLocaleString()}
            sub="followed through to your site"
          />
        </div>

        {/* Secondary metrics */}
        <div style={{ background: SURFACE, border: `1px solid ${BORDER_COLOR}`, borderRadius: 8, padding: '16px 20px', marginBottom: 28, display: 'flex', gap: 32, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8a9bb5', marginBottom: 4 }}>
              Click-Through Rate
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: 'Oswald, sans-serif', color: TEXT_LIGHT }}>
              {metrics.click_through_rate_pct}%
            </div>
            <div style={{ fontSize: '0.75rem', color: '#8a9bb5', marginTop: 2 }}>logo clickers ÷ scoreboard viewers</div>
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8a9bb5', marginBottom: 4 }}>
              Conversion Rate
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: 'Oswald, sans-serif', color: TEXT_LIGHT }}>
              {metrics.redirect_conversion_pct}%
            </div>
            <div style={{ fontSize: '0.75rem', color: '#8a9bb5', marginTop: 2 }}>website visitors ÷ logo clickers</div>
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8a9bb5', marginBottom: 4 }}>
              Total Logo Clicks
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: 'Oswald, sans-serif', color: TEXT_LIGHT }}>
              {metrics.logo_total_clicks?.toLocaleString()}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#8a9bb5', marginTop: 2 }}>including repeat clicks</div>
          </div>
          <div>
            <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#8a9bb5', marginBottom: 4 }}>
              Total Website Clicks
            </div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, fontFamily: 'Oswald, sans-serif', color: TEXT_LIGHT }}>
              {metrics.website_total_redirects?.toLocaleString()}
            </div>
            <div style={{ fontSize: '0.75rem', color: '#8a9bb5', marginTop: 2 }}>including repeat visits</div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ fontSize: '0.75rem', color: '#8a9bb5', borderTop: `1px solid ${BORDER_COLOR}`, paddingTop: 16 }}>
          Generated by DerbyScores · derbyscores.com
        </div>
      </div>

      <style>{`
        @media print {
          .no-print { display: none !important; }
          nav, .navbar, header { display: none !important; }
          body { background: #ffffff !important; color: #0d1b2a !important; }
          .sponsor-report-page { max-width: 100% !important; color: #0d1b2a !important; }
          .sponsor-report-page h1,
          .sponsor-report-page div,
          .sponsor-report-page span { color: #0d1b2a !important; }
          .sponsor-report-page div {
            background: #ffffff !important;
            border-color: #d0d0d0 !important;
          }
        }
      `}</style>
    </>
  );
}
